import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthContext } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import { t } from '../i18n/t';

/**
 * Round 132b (owner decision 19): an existing learner whose profile has no `ageConfirmedAt` is asked ONCE, at their next visit, to confirm "I am 18 or older".
 * New learners confirm it on the profile-setup step (ProfileSetupPage), so this never shows during onboarding. The time is stamped by the SERVER
 * (serverTimestamp(), and firestore.rules refuses any other value and any second write). Someone who is younger can sign out; nothing is deleted by this screen.
 */
export default function AgeConfirmationGate() {
  const { user, profile, refreshProfile, logout } = useAuthContext();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // not signed in / no finished profile yet (the profile-setup step asks it itself) / already confirmed / staff pages
  // Round 141: an account that has not confirmed its e-mail address yet cannot save this line (firestore.rules) - it is saved once the address is confirmed
  if (!user || !profile || !profile.preferredName || profile.ageConfirmedAt || user.emailVerified === false) return null;
  if (pathname.startsWith('/admin') || pathname.startsWith('/onboarding/profile')) return null;

  async function confirm() {
    if (!user) return;
    setBusy(true);
    setError('');
    try {
      await setDoc(doc(db, 'learnerProfiles', user.uid), { ageConfirmedAt: serverTimestamp() }, { merge: true });
      await refreshProfile();
    } catch {
      setError(t('onboarding:ageGate.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="age-gate">
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-labelledby="age-gate-title" className="relative bg-card rounded-3xl shadow-2xl p-6 sm:p-8 max-w-md w-full">
        <h2 id="age-gate-title" className="text-xl font-bold text-ink mb-2">{t('onboarding:ageGate.title')}</h2>
        <p className="text-sm text-muted mb-5">{t('onboarding:ageGate.body')}</p>
        {error && <p role="alert" className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex flex-col gap-3">
          <Button size="lg" fullWidth loading={busy} onClick={confirm}>{t('onboarding:ageGate.confirm')}</Button>
          <Button variant="secondary" fullWidth disabled={busy} onClick={() => { void logout(); }}>{t('onboarding:ageGate.signOut')}</Button>
        </div>
      </div>
    </div>
  );
}
